import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { invoke } from "./tauri-web";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac, openAccountDelete } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { StubMenu } from "./StubMenu";
import { PhotoViewer } from "./PhotoViewer";
import {
  store, todayLocal, formatMoney, parseMoneyToCents, splitList, topArtist, stars,
  COMMON_CURRENCIES, type Show, type LedgerSignals,
} from "./store";
import { recapYear, openWebLogin, BrainError, type BrainErrorCode } from "./brain";
import { buildExport } from "./export";
import { BackupMenu } from "./BackupMenu";
import { OrioraBadge } from "./OrioraBadge";
import Chat from "./chat/Chat";
import emptyFirstShow from "./assets/empty-first-show.svg";
import {
  Sparkles, Trash2, X, Lock, Music, WifiOff, RefreshCw, MapPin, Users, Ticket, Star, CalendarDays, ListMusic,
} from "lucide-react";

const fmtDate = (s: string) =>
  s ? new Date(s.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }) : "";
// Split a show date into the ticket-stub parts (day / MON / year) — the concert-log signature.
function ticketDate(iso: string) {
  const d = new Date(iso.slice(0, 10) + "T00:00:00Z");
  return {
    day: d.getUTCDate(),
    mon: d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" }).toUpperCase(),
    year: iso.slice(0, 4),
  };
}
const LIST_CAP = 50000;
const APP_SCHEME = "oriora-encore";
// SCREENSHOT=0 — sim-only auth bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2/§3, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out. (mirrors expense_audit_ai)
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground. THIS is the reliable catch for
  // "deleted my account, came back, still signed in": returning to the app re-checks the
  // token, the server rejects the deleted account, and we drop to login.
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-encore://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // oriora-encore://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented && !SCREENSHOT) {
    return <AiConsent brand="Oriora Encore"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <div style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2rem", textAlign: "center", margin: "auto" }}>
        <div style={{ fontSize: "2.4rem", marginBottom: "0.4rem", color: "var(--accent)" }}><Music size={40} style={{ verticalAlign: "middle" }} /></div>
        <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: "var(--fs-sm)" }}>Oriora Encore</div>
        <h1 className="serif" style={{ fontSize: "1.5rem", margin: "0.4rem 0 0.4rem" }}>Sign in with Oriora</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, marginBottom: "1.25rem" }}>
          Uses your Oriora account — opens to sign in, then returns here. Your show ledger lives on this
          device; the Year in Live Music recap runs through your Oriora account. Oriora doesn't retain what you send for an AI action. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try {
              const p = platform();
              if (p === "ios") await signInIos(APP_SCHEME);
              else if (p === "macos") await signInMac(APP_SCHEME);
              else if (p === "web") window.location.href = "/login?next=/oriora-encore/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
      </div>
    </main>
  );
}

type Tab = "ledger" | "insights";

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [shows, setShows] = useState<Show[]>([]);
  const [tab, setTab] = useState<Tab>("ledger");
  const [editing, setEditing] = useState<Show | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Show | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const exportRef = useRef<(() => void) | null>(null);

  const reload = useCallback(async () => {
    setShows(await store.listShows({ limit: LIST_CAP }));
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // Native-menu actions (Actions menu in the macOS menu bar): Log / Recap / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "log": setEditing(null); setShowForm(true); break;
        case "reflect": setTab("insights"); break;
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const doExport = useCallback(() => {
    if (shows.length === 0) return;
    const { csv, filename } = buildExport(shows);
    void (async () => { const p = await saveFileDialog({ defaultPath: filename }); if (p) await writeTextFile(p, csv); })();
  }, [shows]);
  exportRef.current = doExport;

  function startLog(s?: Show) { setEditing(s ?? null); setShowForm(true); }
  async function doDelete(id: string) {
    setConfirmDelete(null);
    await store.deleteShow(id);
    await reload();
  }

  const fav = useMemo(() => topArtist(shows), [shows]);

  return (
    <main className="app-shell">
      {offline && (
        <div role="status" className="row" style={{ gap: "0.45rem", fontSize: "var(--fs-xs)", color: "var(--warning)", background: "rgba(217,147,58,0.12)", border: "1px solid rgba(217,147,58,0.35)", borderRadius: 8, padding: "0.45rem 0.75rem", marginBottom: "1rem" }}>
          <WifiOff size={14} style={{ flexShrink: 0 }} /> Recap paused — your shows still save locally
        </div>
      )}

      {/* 🔴 IDENTITY (2026-08-30): there is NO wordmark header row. The most recent NIGHT is the
          header — a gig ledger opens on the last show, not on the app's own name. The backup and
          account controls still have to be reachable, so they collapse into ONE punched stub tab
          riding on that headline. Do not reintroduce a wordmark strip, and do not split the
          control back into two circles. */}
      <ShowHeadline
        show={shows[0] ?? null}
        menu={
          <StubMenu
            backup={
              <BackupMenu
                appName="Encore" fileSlug="encore" appScheme="oriora-encore"
                hasData={shows.length > 0} onRestored={reload}
                onExportCsv={shows.length > 0 ? doExport : undefined}
              />
            }
            account={
              <AccountMenu email={email} appSlug="oriora-encore" appName="Oriora Encore" onSignedOut={onSignedOut} />
            }
          />
        }
      />

      {/* R1 — THE FAST LOG LEADS. "I was there" in seconds; everything else optional after. */}
      <QuickLog onSaved={reload} onMore={() => startLog()} />

      {/* R3 — the year identity, building live from the log (observational, never advice). */}
      <LedgerHero count={shows.length} fav={fav} shows={shows} />

      {/* Ruled tabs: the catalog (Ledger) vs the recap (Insights) */}
      <AskTear onAsk={() => setChatOpen(true)} />

      <div style={{ margin: "0.9rem 0 0.75rem" }}>
        <div className="tabs" role="tablist">
          <button role="tab" aria-selected={tab === "ledger"} className={`tab${tab === "ledger" ? " active" : ""}`} onClick={() => setTab("ledger")}>Ledger<span className="count tnum">{shows.length || ""}</span></button>
          <button role="tab" aria-selected={tab === "insights"} className={`tab${tab === "insights" ? " active" : ""}`} onClick={() => setTab("insights")}>Insights</button>
        </div>
      </div>

      {tab === "ledger" ? (
        <Ledger shows={shows} onAdd={() => startLog()} onEdit={startLog} onDelete={(s) => setConfirmDelete(s)} />
      ) : (
        <Insights shows={shows} />
      )}

      {showForm && (
        <ShowFormModal editing={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); void reload(); }} />
      )}

      {confirmDelete && (
        <ModalShell title="Remove this show?" onCancel={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0 0 1rem" }}>
            Remove {confirmDelete.artists.join(", ")} on {fmtDate(confirmDelete.seen_on)} from your ledger? This can’t be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem" }}>
            <button className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-primary" style={{ fontSize: "var(--fs-xs)", background: "var(--rose)", borderColor: "var(--rose)" }} onClick={() => doDelete(confirmDelete.id)}>Remove</button>
          </div>
        </ModalShell>
      )}

      {/* 🔴 IDENTITY (2026-08-30): the floating "✨ Chat" pill that used to launch this was DELETED —
          same pill, same corner, same label in ~15 sibling apps. The assistant is NOT removed and
          must never be: its entry is the .oen-tear tear-off strip on the ledger edge, which calls
          this same setChatOpen. If a future change moves it, REPLACE it — never drop it. */}
      <aside className={`oen-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="oen-head">
          <span className="row" style={{ gap: "0.35rem" }}>
            <Sparkles size={14} /> Assistant — type to log, find, rate, or recap
          </span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </main>
  );
}

/** R1 — the fast log: artist, venue, city, date, "I was there". The category's <30s ritual. */
// 🔴 IDENTITY (2026-08-30): the most recent NIGHT is the header. Replaces the "🎟 Encore"
// wordmark row + two circular controls. A ledger of gigs opens on the last gig.
function ShowHeadline({ show, menu }: { show: Show | null; menu: React.ReactNode }) {
  return (
    <div className="oen-headline">
      <div style={{ flex: 1, minWidth: 0, paddingRight: 44 }}>
        <div className="oen-headline-lbl">{show ? "Last night out" : "Your gig ledger"}</div>
        <div className="oen-headline-art">{show ? show.artists.join(", ") : "Nothing logged yet"}</div>
        <div className="oen-headline-sub">
          {show
            ? `${fmtDate(show.seen_on)}${show.venue ? ` · ${show.venue}` : ""}${show.city ? `, ${show.city}` : ""}`
            : "Log a show and it leads this screen."}
        </div>
      </div>
      <div className="oen-headline-menu">{menu}</div>
    </div>
  );
}

// 🔴 THE ASSISTANT ENTRY — a tear-off strip on the ledger's edge, replacing the floating "Chat"
// pill ~15 siblings shared. Rendered on home in every state so the AI is always reachable, and
// outside the Ledger/Insights switch so changing tab never hides it. onAsk opens the same sheet.
function AskTear({ onAsk }: { onAsk: () => void }) {
  return (
    <button type="button" className="oen-tear" onClick={onAsk} aria-label="Open chat assistant">
      <span className="oen-tear-notch l" aria-hidden="true" />
      <span className="oen-tear-notch r" aria-hidden="true" />
      <span className="oen-tear-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none">
          <path d="M3.4 7.2h17.2v3.1a1.9 1.9 0 0 0 0 3.4v3.1H3.4v-3.1a1.9 1.9 0 0 0 0-3.4z"
                stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M12 8.4v1.7M12 13.9v1.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </span>
      <span className="oen-tear-txt">
        <span className="oen-tear-lbl">Ask</span>
        <span className="oen-tear-sub">log · find · rate · recap</span>
      </span>
    </button>
  );
}

function QuickLog({ onSaved, onMore }: { onSaved: () => void; onMore: () => void }) {
  const [artist, setArtist] = useState("");
  const [venue, setVenue] = useState("");
  const [city, setCity] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [fest, setFest] = useState(false);
  const [saving, setSaving] = useState(false);
  // READY-STATE 2026-09-19 — this used to file the whole field as ONE artist (`[artist.trim()]`),
  // so a festival bill typed the way people actually type it ("Fontaines D.C., The National,
  // Idles") became a single artist whose name contained commas: unsearchable, and it broke the
  // most-seen count. Splitting on commas is what makes PARITY-MATRIX row 2 real — the free-text
  // lineup that answers Concert Archives' loudest complaint (it auto-selects every act on a
  // festival bill with no deselect-all). `splitList` is the app's own helper, already used by the
  // full editor, so the quick door and the detailed form now produce the same shape.
  const acts = splitList(artist);
  async function save() {
    if (acts.length === 0 || saving) return;
    setSaving(true);
    try {
      await store.createShow({
        artists: acts, venue: venue.trim(), city: city.trim(), seen_on: date,
        is_festival: fest,
      });
      setArtist(""); setVenue(""); setCity(""); setFest(false);
      onSaved();
    } finally { setSaving(false); }
  }
  return (
    <section className="qlog" aria-label="Log a show">
      <div className="qlog-head">
        <span className="qlog-title">I was there</span>
        <span className="kicker">the ledger</span>
      </div>
      <input className="qlog-artist" placeholder="Who did you see?" value={artist}
        aria-label="Who did you see? Separate a festival lineup with commas"
        onChange={(e) => setArtist(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void save(); } }} />
      {acts.length > 1 && (
        <div className="qlog-acts" aria-live="polite">
          {acts.map((a) => <span key={a} className="qlog-act">{a}</span>)}
          <span className="qlog-actn tnum">{acts.length} acts</span>
        </div>
      )}
      <div className="qlog-grid">
        <input placeholder="Venue" value={venue} onChange={(e) => setVenue(e.target.value)} />
        <input placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
        <input className="span-2" type="date" aria-label="Date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div className="qlog-cta">
        <button className="btn btn-primary" disabled={acts.length === 0 || saving} onClick={() => void save()}>
          <Ticket size={15} /> {saving ? "Filing…" : "I was there"}
        </button>
        <button type="button" className={`chip${fest ? " selected" : ""}`} aria-pressed={fest}
          onClick={() => setFest(!fest)}>Festival</button>
        <button type="button" className="qlog-more" onClick={onMore}>More details…</button>
      </div>
      <p className="qlog-hint">
        A festival? Type the lineup with commas — every act is filed separately, and you can mark
        the whole night as a festival. Rating, companions, cost and notes can be added any time.
      </p>
    </section>
  );
}

/** R3 — the year identity: this year's count, most-seen, venues returned to. Descriptive only. */
function LedgerHero({ count, fav, shows }: { count: number; fav: { name: string; count: number } | null; shows: Show[] }) {
  const yr = new Date().getFullYear().toString();
  const thisYear = useMemo(() => shows.filter((s) => s.seen_on.startsWith(yr)).length, [shows, yr]);
  const venuesReturned = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of shows) if (s.venue) m.set(s.venue, (m.get(s.venue) ?? 0) + 1);
    return [...m.values()].filter((n) => n >= 2).length;
  }, [shows]);
  // READY-STATE 2026-09-19 — this read used to `return null` at zero shows, so the one thing the
  // researched peers do not have (PARITY-MATRIX row 3, per-show spend with the festival-vs-gig
  // split) was switched off for exactly the person who has never seen it. The ledger now shows
  // its rule with zeros in it, so a new arrival can see what fills.
  const spend = useMemo(() => {
    const byCur = new Map<string, number>();
    for (const s of shows) {
      if (!s.seen_on.startsWith(yr) || s.cost === null) continue;
      const cur = s.currency || "USD";
      byCur.set(cur, (byCur.get(cur) ?? 0) + s.cost);
    }
    const top = [...byCur.entries()].sort((a, b) => b[1] - a[1])[0];
    return top ? { cur: top[0], cents: top[1] } : null;
  }, [shows, yr]);
  const split = useMemo(() => {
    let fest = 0, gig = 0;
    for (const s of shows) {
      if (!s.seen_on.startsWith(yr)) continue;
      if (s.is_festival) fest++; else gig++;
    }
    return { fest, gig };
  }, [shows, yr]);

  return (
    <div className="hero" style={{ marginTop: "1rem" }} aria-label="Your year in live music so far">
      <div className="yearline">
        <div className="yearline-item">
          <div className="yearline-num accent tnum">{thisYear}</div>
          <div className="yearline-lbl">show{thisYear !== 1 ? "s" : ""} in {yr}</div>
        </div>
        <div className="yearline-item">
          <div className="yearline-num">{fav ? fav.name : "—"}</div>
          <div className="yearline-lbl">{fav ? `most-seen · ${fav.count}×` : "most-seen"}</div>
        </div>
        <div className="yearline-item">
          <div className="yearline-num tnum">{venuesReturned}</div>
          <div className="yearline-lbl">venue{venuesReturned !== 1 ? "s" : ""} returned to</div>
        </div>
      </div>

      {/* The spend rule — row 3. Torn off the yearline like a total at the foot of a ledger. */}
      <div className="yearline-rule">
        <span className="yearline-rule-fig tnum">{spend ? formatMoney(spend.cents, spend.cur) : "—"}</span>
        <span className="yearline-rule-txt">
          {spend
            ? <>on live music in {yr}{split.fest + split.gig > 0 ? <> · <span className="tnum">{split.fest}</span> festival{split.fest === 1 ? "" : "s"} · <span className="tnum">{split.gig}</span> gig{split.gig === 1 ? "" : "s"}</> : null}</>
            : <>Add what a ticket cost and this totals your year — festivals and gigs kept apart.</>}
        </span>
      </div>
    </div>
  );
}

// ── Ledger (the catalog / logging screen) ───────────────────────────────────────
function Ledger({ shows, onAdd, onEdit, onDelete }: {
  shows: Show[]; onAdd: () => void; onEdit: (s: Show) => void; onDelete: (s: Show) => void;
}) {
  // Group shows by year for a clean catalog.
  const groups = useMemo(() => {
    const m = new Map<string, Show[]>();
    for (const s of shows) {
      const y = s.seen_on.slice(0, 4) || "—";
      (m.get(y) ?? m.set(y, []).get(y)!).push(s);
    }
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [shows]);

  if (shows.length === 0) {
    return (
      <div className="card empty" style={{ marginTop: "1rem" }}>
        <img src={emptyFirstShow} alt="" aria-hidden="true" width={280} />
        <div className="empty-title">Your first stub</div>
        <div className="empty-copy">A gig or a festival you were at — who, where, when. The stub files itself above; details can wait.</div>
        <button className="btn btn-primary" style={{ marginTop: "1rem" }} onClick={onAdd}>Log with details</button>
      </div>
    );
  }

  return (
    <>
      {groups.map(([year, list]) => (
        <div key={year} style={{ marginBottom: "1.25rem" }}>
          <div className="yr-head">
            <span className="yr-num">{year}</span>
            <span className="yr-count">{list.length} {list.length === 1 ? "show" : "shows"}</span>
            <span className="yr-line" />
          </div>
          <div className="stub-list">
            {list.map((s) => <ShowTile key={s.id} s={s} onEdit={() => onEdit(s)} onDelete={() => onDelete(s)} />)}
          </div>
        </div>
      ))}
    </>
  );
}

// A show renders as a TICKET STUB — the concert-log domain signature (cf. Momento/Concert Archives):
// artist + venue on the ticket body, a perforated tear, and a big stacked date on the right stub.
function ShowTile({ s, onEdit, onDelete }: { s: Show; onEdit: () => void; onDelete: () => void }) {
  const [zoom, setZoom] = useState(false);
  const place = [s.venue, s.city].filter(Boolean).join(" · ");
  const dt = ticketDate(s.seen_on);
  const headliner = s.artists[0] ?? "";
  const support = s.artists.slice(1).join(", ");
  return (
    <div className={`stub${s.rating === 5 ? " top" : ""}`} onClick={onEdit}>
      <div className="stub-body">
        <div className="row" style={{ gap: "0.6rem", flexWrap: "nowrap", minWidth: 0 }}>
          {s.photo && (
            <img className="stub-photo" src={s.photo} alt={`Photo from ${headliner}`} title="Tap to view"
              onClick={(ev) => { ev.stopPropagation(); setZoom(true); }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="stub-art">
              {headliner}
              {s.is_festival && <span className="stub-fest">festival</span>}
            </div>
            {support && <div className="stub-support">with {support}</div>}
            {place && (
              <div className="stub-place">
                <MapPin size={11} style={{ flexShrink: 0 }} /><span>{place}</span>
              </div>
            )}
          </div>
        </div>
        <div className="stub-meta">
          {s.rating > 0 && <span className="stub-stars" title={`${s.rating}/5`}>{stars(s.rating)}</span>}
          {s.cost !== null && <span className="stub-cost">{formatMoney(s.cost, s.currency)}</span>}
          {s.companions.length > 0 && <span className="stub-with"><Users size={11} /> {s.companions.length}</span>}
          <button className="stub-del" title="Remove show" aria-label="Remove show"
            onClick={(ev) => { ev.stopPropagation(); onDelete(); }}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      <span className="stub-perf" aria-hidden="true" />
      <div className="stub-date">
        <div className="stub-day">{dt.day}</div>
        <div className="stub-mon">{dt.mon}</div>
        <div className="stub-yr">{dt.year}</div>
      </div>
      {zoom && s.photo && (
        <PhotoViewer photos={[{ src: s.photo, alt: `Photo from ${headliner}` }]} onClose={() => setZoom(false)} />
      )}
    </div>
  );
}

// ── Insights / Recap screen (the AI feature + the derived-signal evidence) ──────
type ReflectErr = { code: BrainErrorCode | "empty"; message: string };

function Insights({ shows }: { shows: Show[] }) {
  const [recap, setRecap] = useState<string | null>(null);
  const [signals, setSignals] = useState<LedgerSignals | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ReflectErr | null>(null);

  // Always compute the on-device signals so the Insights numbers render even before
  // (or without) an AI recap — the moat is visible locally; the AI just narrates it.
  useEffect(() => {
    let alive = true;
    if (shows.length === 0) { setSignals(null); return; }
    void store.computeSignals(todayLocal()).then((s) => { if (alive) setSignals(s); });
    return () => { alive = false; };
  }, [shows]);

  async function run() {
    setBusy(true); setErr(null); setRecap(null);
    try {
      const sig = await store.computeSignals(todayLocal());
      setSignals(sig);
      const r = await recapYear(sig, "all");
      const text = r.recap.trim();
      if (!text) setErr({ code: "empty", message: "Not enough logged shows to recap yet — add a few more." });
      else setRecap(text);
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Recap failed — please try again." });
    } finally { setBusy(false); }
  }

  if (shows.length === 0) {
    return (
      <div className="card" style={{ padding: "2.5rem 1.5rem", textAlign: "center", marginTop: "1rem" }}>
        <div style={{ color: "var(--accent)" }}><Sparkles size={40} /></div>
        <div className="serif" style={{ fontSize: "1.2rem", margin: "0.5rem 0 0.25rem" }}>Your Year in Live Music</div>
        <div className="muted" style={{ fontSize: "var(--fs-sm)" }}>Log a few shows and Encore will recap your live-music year — counts, venues, most-seen artists, spend, the gaps.</div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: "1rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "1rem" }}>
      {signals && <QuickStats signals={signals} />}

      {/* The ONE AI feature: Year in Live Music recap */}
      <div className="card" style={{ padding: "1rem 1.25rem", borderLeft: "3px solid var(--accent)" }}>
        <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem" }}>
          <div className="row" style={{ fontWeight: 600, fontSize: "var(--fs-sm)", gap: "0.35rem", flexWrap: "wrap" }}>
            <Sparkles size={14} style={{ color: "var(--accent)", flexShrink: 0 }} /> Year in Live Music
          </div>
          <button className="btn btn-primary row" style={{ fontSize: "var(--fs-xs)", padding: "0.35rem 0.7rem", gap: "0.35rem", flexShrink: 0 }} disabled={busy} onClick={run}>
            {busy ? "Writing…" : recap ? (<><RefreshCw size={12} /> Refresh</>) : "Write my recap"}
          </button>
        </div>

        {!busy && !recap && !err && (
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 6 }}>
            A warm recap of your live-music year — the counts, your most-seen artists, where your spend went, the longest gap.
          </div>
        )}

        {busy && (
          <div aria-live="polite" style={{ marginTop: "0.75rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
            <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
              <Sparkles size={12} style={{ color: "var(--accent)" }} /> Reading your show ledger…
            </span>
            <div className="skeleton-line" style={{ width: "100%" }} />
            <div className="skeleton-line" style={{ width: "94%" }} />
            <div className="skeleton-line" style={{ width: "72%" }} />
          </div>
        )}

        {err && !busy && <ReflectionError err={err} onRetry={run} />}

        {recap && !busy && (
          <div style={{ marginTop: "0.6rem" }} aria-live="polite">
            <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.6, margin: 0 }}>{recap}</p>
            <div style={{ marginTop: 8 }}><OrioraBadge /></div>
            <div className="row" style={{ marginTop: 10 }}>
              <span className="muted" style={{ fontSize: 10 }}>Describes shows you’ve logged — not advice, and never about buying or reselling tickets.</span>
            </div>
          </div>
        )}
      </div>

      {signals && <DerivedDetail signals={signals} />}
    </div>
  );
}

function ReflectionError({ err, onRetry }: { err: ReflectErr; onRetry: () => void }) {
  if (err.code === "insufficient_funds") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "rgba(217,147,58,0.1)", border: "1px solid rgba(217,147,58,0.3)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600 }}>Add funds to keep recapping</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then write the recap again.</div>
        </div>
      </div>
    );
  }
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "var(--elev-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "empty") {
    return <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 10 }}>{err.message}</div>;
  }
  const retryable = err.code === "unavailable" || err.code === "unauthenticated";
  return (
    <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontSize: "var(--fs-xs)", color: "var(--rose)" }}>{err.message}</span>
      {retryable && err.code === "unavailable" && (
        <button className="btn row" style={{ fontSize: "var(--fs-xs)", padding: "0.25rem 0.55rem", gap: "0.3rem", flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

// Headline numbers — the moat, computed on-device, shown WITHOUT any AI call.
function QuickStats({ signals }: { signals: LedgerSignals }) {
  const o = signals.overall;
  const spend = o.total_spend_cents !== null ? formatMoney(o.total_spend_cents, signals.currency) : "—";
  const stats: { num: string; lbl: string }[] = [
    { num: String(o.total_shows), lbl: "shows" },
    { num: String(o.distinct_artists), lbl: "artists" },
    { num: String(o.distinct_venues), lbl: "venues" },
    { num: spend, lbl: "total spend" },
  ];
  return (
    <div className="stat-grid">
      {stats.map((s) => (
        <div className="stat" key={s.lbl}>
          <div className="num tnum">{s.num}</div>
          <div className="lbl">{s.lbl}</div>
        </div>
      ))}
    </div>
  );
}

// The receipts: the actual derived numbers the recap drew from — observational only.
function DerivedDetail({ signals }: { signals: LedgerSignals }) {
  const maxYear = Math.max(1, ...signals.by_year.map((y) => y.shows));
  const maxArtist = Math.max(1, ...signals.top_artists.map((a) => a.count));
  const maxVenue = Math.max(1, ...signals.top_venues.map((v) => v.count));
  const sb = signals.spend_breakdown;
  const gap = signals.longest_gap;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "1rem" }}>
      {/* Shows per year */}
      {signals.by_year.length > 0 && (
        <div className="card" style={{ padding: "1.1rem 1.25rem" }}>
          <SectionTitle icon={<CalendarDays size={15} />} text="Shows per year" />
          <div className="year-bars" style={{ marginTop: "0.7rem" }}>
            {[...signals.by_year].reverse().map((y) => {
              const pct = (y.shows / maxYear) * 100;
              return (
                <div className="year-col" key={y.year} title={`${y.year}: ${y.shows} shows`}>
                  <span className="year-val tnum">{y.shows}</span>
                  <div className="year-bar" style={{ height: `${pct}%` }} />
                  <span className="year-lbl">{`'${String(y.year).slice(2)}`}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Most-seen artists */}
      {signals.top_artists.length > 0 && (
        <div className="card" style={{ padding: "1.1rem 1.25rem" }}>
          <SectionTitle icon={<Music size={15} />} text="Most-seen artists" />
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem", marginTop: "0.7rem" }}>
            {signals.top_artists.slice(0, 6).map((a) => (
              <div className="freq-row" key={a.name}>
                <span className="freq-name">{a.name}</span>
                <span className="freq-track"><span className="freq-fill" style={{ width: `${(a.count / maxArtist) * 100}%` }} /></span>
                <span className="freq-count">{a.count}×</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top venues */}
      {signals.top_venues.length > 0 && (
        <div className="card" style={{ padding: "1.1rem 1.25rem" }}>
          <SectionTitle icon={<MapPin size={15} />} text="Venues you return to" />
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem", marginTop: "0.7rem" }}>
            {signals.top_venues.slice(0, 5).map((v) => (
              <div className="freq-row" key={v.name}>
                <span className="freq-name">{v.name}</span>
                <span className="freq-track"><span className="freq-fill" style={{ width: `${(v.count / maxVenue) * 100}%` }} /></span>
                <span className="freq-count">{v.count}×</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Spend split + longest gap + ratings — observational chips */}
      <div className="card" style={{ padding: "1.1rem 1.25rem" }}>
        <SectionTitle icon={<Sparkles size={15} />} text="Patterns in your ledger" />
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.7rem" }}>
          {(sb.festival_shows_with_cost > 0 || sb.regular_shows_with_cost > 0) && sb.bigger_bucket && (
            <Chip title={`Festivals ${formatMoney(sb.festival_spend_cents, signals.currency)} vs regular shows ${formatMoney(sb.regular_spend_cents, signals.currency)}`}
              label={sb.bigger_bucket === "even" ? "spend even" : `${sb.bigger_bucket} cost most`} />
          )}
          {gap && (
            <Chip title={`From ${fmtDate(gap.from_date)} to ${fmtDate(gap.to_date)}`} label={`longest gap ${gap.days}d`} />
          )}
          {signals.year_shift && (
            <Chip title={`${signals.year_shift.recent_year} vs ${signals.year_shift.prior_year}`}
              label={`${signals.year_shift.recent_year}: ${signals.year_shift.direction === "same" ? "same" : `${signals.year_shift.shows_delta > 0 ? "+" : ""}${signals.year_shift.shows_delta}`} shows`} />
          )}
          {signals.overall.festival_count > 0 && (
            <Chip title="Festivals logged" label={`${signals.overall.festival_count} festival${signals.overall.festival_count === 1 ? "" : "s"}`} />
          )}
          {signals.ratings.rated_shows > 0 && signals.ratings.avg_rating !== null && (
            <Chip title={`Across ${signals.ratings.rated_shows} rated shows`} label={`avg ${signals.ratings.avg_rating.toFixed(1)}★`} />
          )}
          {signals.top_companions.length > 0 && (
            <Chip title="Person you've gone with most" label={`most with ${signals.top_companions[0].name}`} />
          )}
        </div>
        {signals.ratings.best_rated && signals.ratings.best_rated.rating >= 4 && (
          <div className="row" style={{ gap: "0.4rem", marginTop: "0.8rem", fontSize: "var(--fs-xs)", color: "var(--ink-2)" }}>
            <Star size={13} style={{ color: "var(--star)" }} />
            Highest-rated: {signals.ratings.best_rated.artists}{signals.ratings.best_rated.venue ? ` @ ${signals.ratings.best_rated.venue}` : ""} ({signals.ratings.best_rated.rating}★)
          </div>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="row" style={{ gap: "0.4rem" }}>
      <span style={{ color: "var(--accent)", display: "inline-flex" }}>{icon}</span>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{text}</div>
    </div>
  );
}

function Chip({ label, title }: { label: string; title: string }) {
  return (
    <span className="chip tnum" title={title}
      style={{ padding: "0.15rem 0.5rem", fontSize: 11, background: "var(--accent-soft)", borderColor: "transparent", color: "var(--accent-soft-ink)" }}>
      {label}
    </span>
  );
}

// ── Log / edit a show ────────────────────────────────────────────────────────────
function ShowFormModal({ editing, onClose, onSaved }: { editing: Show | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [artists, setArtists] = useState(editing ? editing.artists.join(", ") : "");
  const [seenOn, setSeenOn] = useState(editing?.seen_on ?? todayLocal());
  const [venue, setVenue] = useState(editing?.venue ?? "");
  const [city, setCity] = useState(editing?.city ?? "");
  const [cost, setCost] = useState(editing && editing.cost !== null ? (editing.cost / 100).toString() : "");
  const [currency, setCurrency] = useState(editing?.currency ?? "USD");
  const [companions, setCompanions] = useState(editing ? editing.companions.join(", ") : "");
  const [setlist, setSetlist] = useState(editing?.setlist_notes ?? "");
  const [rating, setRating] = useState<number>(editing?.rating ?? 0);
  const [isFestival, setIsFestival] = useState<boolean>(editing?.is_festival ?? false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    const artistList = splitList(artists);
    if (artistList.length === 0) { setErr("Add at least one artist."); return; }
    setSaving(true); setErr(null);
    const costCents = cost.trim() ? parseMoneyToCents(cost) : null;
    try {
      if (isEditing) {
        await store.updateShow(editing!.id, {
          artists: artistList, seen_on: seenOn, venue, city,
          cost: costCents, cost_set: true, currency,
          companions: splitList(companions), setlist_notes: setlist, rating, is_festival: isFestival,
        });
      } else {
        await store.createShow({
          artists: artistList, seen_on: seenOn, venue, city,
          cost: costCents, currency,
          companions: splitList(companions), setlist_notes: setlist, rating, is_festival: isFestival,
        });
      }
      onSaved();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg === "artist_required" ? "Add at least one artist." : `Could not save — try again. (${msg})`);
      setSaving(false);
    }
  }

  return (
    <ModalShell title={isEditing ? "Edit show" : "Log a show"} onCancel={onClose} wide>
      <Field label="Artist(s)" hint="Headliner first; separate support acts with commas">
        <input placeholder="e.g. The National, Bombay Bicycle Club" value={artists} onChange={(e) => setArtists(e.target.value)} autoFocus />
      </Field>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <Field label="Date" grow>
          <input type="date" value={seenOn} onChange={(e) => setSeenOn(e.target.value)} />
        </Field>
        <Field label="Festival?" grow>
          <button type="button" className={`chip${isFestival ? " selected" : ""}`} style={{ width: "100%", justifyContent: "center", padding: "0.55rem" }} onClick={() => setIsFestival((v) => !v)}>
            {isFestival ? "Yes — festival" : "No — single show"}
          </button>
        </Field>
      </div>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <Field label="Venue" grow>
          <input placeholder="e.g. Brixton Academy" value={venue} onChange={(e) => setVenue(e.target.value)} />
        </Field>
        <Field label="City" grow>
          <input placeholder="e.g. London" value={city} onChange={(e) => setCity(e.target.value)} />
        </Field>
      </div>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <Field label="What you paid" grow hint="Leave blank if free / unknown">
          <input inputMode="decimal" placeholder="0.00" value={cost} onChange={(e) => setCost(e.target.value)} />
        </Field>
        <Field label="Currency" grow>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {COMMON_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            {!COMMON_CURRENCIES.includes(currency as (typeof COMMON_CURRENCIES)[number]) && <option value={currency}>{currency}</option>}
          </select>
        </Field>
      </div>

      <Field label="Who you went with" hint="Optional — separate names with commas">
        <input placeholder="e.g. Alex, Sam" value={companions} onChange={(e) => setCompanions(e.target.value)} />
      </Field>

      <Field label="Your rating">
        <div className="star-row" role="radiogroup" aria-label="Rating">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" className={`star-btn${rating >= n ? " on" : ""}`} aria-label={`${n} star${n === 1 ? "" : "s"}`} aria-pressed={rating >= n}
              onClick={() => setRating(rating === n ? 0 : n)}>★</button>
          ))}
          {rating > 0 && <button type="button" className="btn-ghost" style={{ fontSize: "var(--fs-xs)", marginLeft: 6 }} onClick={() => setRating(0)}>clear</button>}
        </div>
      </Field>

      <Field label="Setlist / notes" hint="Stays on your device unless you back it up — not sent to the AI">
        <textarea rows={3} placeholder="Opener, the encore, who you ran into…" value={setlist} onChange={(e) => setSetlist(e.target.value)} />
      </Field>

      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : isEditing ? "Update" : "Save show"}</button>
      </div>
    </ModalShell>
  );
}

function Field({ label, hint, children, grow }: { label: string; hint?: string; children: React.ReactNode; grow?: boolean }) {
  return (
    <div style={{ marginBottom: "0.9rem", flex: grow ? 1 : undefined, minWidth: grow ? 0 : undefined, width: grow ? undefined : "100%" }}>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.35rem" }}>
        {label}{hint && <span className="muted" style={{ fontWeight: 400, marginLeft: 6, fontSize: "var(--fs-xs)" }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function ModalShell({ title, children, onCancel, wide }: { title: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(31,27,46,0.45)", display: "grid", placeItems: "center", zIndex: 50, padding: "1rem" }}>
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: wide ? 460 : 340, padding: "1.5rem", boxShadow: "var(--shadow-2)", borderRadius: "var(--radius-dialog)", maxHeight: "90vh", overflowY: "auto" }}>
        <div className="serif" style={{ fontWeight: 600, fontSize: "1.1rem", marginBottom: "1rem" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}
